// ============================================================
// bot.ts — Word-playing bot whose strength is set by two simple knobs:
//   1. how often it plays (max words per second, derived from Elo)
//   2. how big its vocabulary is (random sample of the full dictionary)
// ============================================================

import { sampleWordsCapped } from './dictionary';
import { getLangConfig } from '../i18n';

export interface BotProfile {
  elo: number;
  tickMs: number;       // mean time between scoring attempts
  vocabSize: number;    // size of the bot's dictionary subset
  maxWordLen: number;   // word-length ceiling for the bot's pool (2..8)
}

// Map Elo → profile. Three knobs scaled together so a beginner bot is genuinely
// beatable and a maxed-out bot is a wall:
//   - tickMs: 10s/word at floor → 1s/word at ceiling.
//   - vocabSize: 30 words at floor → ~50k at ceiling, exponential.
//   - maxWordLen: 3 letters at floor → 8 at ceiling, linear-ish.
//
// The length cap is the key fix for "I can't climb out of low Elo": a pure
// vocab-size cap still lets a low-Elo bot roll the random 8-letter rarities
// nobody can match. Capping length forces low-Elo bots into the short common
// words a beginner can also see, so the player can actually win and rate up.
export function profileFromElo(elo: number): BotProfile {
  const e = Math.max(400, Math.min(2400, elo));
  const t = (e - 400) / 2000; // 0..1

  // Linear from 10000ms (1 / 10s) at 400 → 1000ms (1/s) at 2400.
  const tickMs = Math.round(10000 - t * 9000);

  // Exponential vocab so a few hundred Elo unlocks a meaningful jump.
  // 30 words at 400 → ~50000 at 2400.
  const vocabSize = Math.round(30 * Math.pow(50000 / 30, t));

  // Length tier: 3 at 400 → 8 at 2400. Clamped to [3, 8] (the dict only stores
  // 2..8-letter words, and we want the bot to play scoring-worthy 3+ letters).
  const maxWordLen = Math.max(3, Math.min(8, 3 + Math.round(t * 5)));

  return { elo: e, tickMs, vocabSize, maxWordLen };
}

function scoreWord(word: string): number {
  const cfg = getLangConfig();
  let base = 0;
  for (const ch of word.toUpperCase()) base += cfg.letterPoints[ch] ?? 0;
  const len = word.length;
  const mult = len >= 7 ? 1.5 : len >= 5 ? 1.25 : 1;
  return Math.round(base * mult);
}

export interface BotInstance {
  stop: () => void;
  getElo: () => number;
  getVocabSize: () => number;
}

// Start a bot. The vocabulary is sampled once at start (length-capped, then
// vocab-size-capped) and the bot only ever plays words from that subset, so
// its strength is shaped by both `vocabSize` and `maxWordLen`.
export function startBot(elo: number, onScore: (points: number, word: string) => void): BotInstance {
  const profile = profileFromElo(elo);
  const vocab = sampleWordsCapped(profile.vocabSize, profile.maxWordLen);
  let stopped = false;

  const tick = () => {
    if (stopped) return;
    if (vocab.length > 0) {
      const word = vocab[Math.floor(Math.random() * vocab.length)];
      onScore(scoreWord(word), word);
    }
    // Slight jitter so the rate is the *mean*, not metronomic.
    const jitter = profile.tickMs * (0.7 + Math.random() * 0.6);
    setTimeout(tick, jitter);
  };
  setTimeout(tick, profile.tickMs);

  return {
    stop: () => { stopped = true; },
    getElo: () => profile.elo,
    getVocabSize: () => vocab.length,
  };
}
